import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { NotifierService } from '../../../_services/notifier.service';
import { APIService } from '../../../api/api.service';
import * as moment from 'moment';

declare let window: any;
declare let $: any;

interface MasterAccount {
  accId: string;
  month: string;
  year: string;
  status: 'ready' | 'loading' | 'success' | 'error';
  statusMessage: string;
}

interface APIResponse {
  presignedUrls?: string[];
  accIds?: string[];
  message?: string;
  error_message?: string;
  msg?: string;
  status?: number;
}

@Component({
  selector: 'app-invoice-report',
  templateUrl: './invoice-report.component.html',
  styleUrls: ['./invoice-report.component.css']
})
export class InvoiceReportComponent implements OnInit, OnDestroy {

  // ─── State ────────────────────────────────────────────────────────────────────
  masterAccounts: MasterAccount[] = [];
  filteredAccounts: MasterAccount[] = [];
  searchQuery: string = '';
  isLoadingAccounts = true;
  isDownloadingConsolidated = false;
  consolidatedStatus: 'ready' | 'loading' | 'success' | 'error' = 'ready';
  consolidatedStatusMessage: string = '';
  dateRange: string = moment().format('MM/YYYY');
  consolidatedForm: FormGroup;

  // ─── Auth ─────────────────────────────────────────────────────────────────────
  userId = localStorage.getItem('eId');
  ut = localStorage.getItem('ut');
  urlPrefix: any = localStorage.getItem('role') == 'Admin' ? 'admin' : 'client';
  writeAccess: boolean =
    localStorage.getItem('acT') == 'readandwrite' ||
    this.urlPrefix == 'admin' ||
    localStorage.getItem('ut') == 'admin';
  accountId: any;
  regionId: any;

  // ─── Private ──────────────────────────────────────────────────────────────────
  private apiURL = `https://api.swayam.cloud/v3/admin/billing/fetchchildaccwithcosts/getInvoices`;
  private currentMessage: any;
  private destroy$ = new Subject<void>();
  private isLoadingMasterAccounts = false; // ✓ Prevent duplicate calls

  constructor(
    private fb: FormBuilder,
    public notifier: NotifierService,
    private zone: NgZone,
    private apiService: APIService
  ) {
    this.consolidatedForm = this.fb.group({
      month: [moment().format('MM'), Validators.required],
      year: [moment().format('YYYY'), Validators.required]
    });
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.accountId = localStorage.getItem('accountId');
    this.regionId = localStorage.getItem('regionId');

    window.angularComponentReference = {
      zone: this.zone,
      componentFn: () => this.onConsolidatedDateChange(),
      component: this,
    };

    $('#consolidatedMonthDate')
      .datepicker({
        autoclose: true,
        minViewMode: 1,
        format: 'mm/yyyy',
        orientation: 'bottom auto',
        endDate: new Date(),
      })
      .on('changeDate', function () {
        window.angularComponentReference.zone.run(() => {
          window.angularComponentReference.componentFn();
        });
      });

    // ✓ Setup notifier FIRST to catch initial messages
    this.setupNotifierSubscription();
    
    // ✓ Load accounts AFTER notifier is ready
    this.loadMasterAccounts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  // ─── Notifier Subscription ────────────────────────────────────────────────────
  private setupNotifierSubscription(): void {
    this.currentMessage = this.notifier.currentMessage.subscribe((msg: string) => {
      try {
        const d = JSON.parse(msg);
        if (d.value == null) return;
        
        // ✓ Only reload if value actually changed
        if (d.key == 'accountId' && this.accountId !== d.value) {
          this.accountId = d.value;
          this.loadMasterAccounts();
        } else if (d.key == 'regionId' && this.regionId !== d.value) {
          this.regionId = d.value;
          this.loadMasterAccounts();
        }
      } catch (error) {
        console.error('Error parsing notifier message:', error);
      }
    });
  }

  // ─── Consolidated Datepicker ──────────────────────────────────────────────────
  onConsolidatedDateChange(): void {
    const raw: string = $('#consolidatedMonthDate').val();
    if (!raw) return;
    const parsed = moment(raw, 'MM/YYYY');
    this.consolidatedForm.patchValue({
      month: parsed.format('MM'),
      year: parsed.format('YYYY')
    });
  }

  // ─── List Masters ─────────────────────────────────────────────────────────────
  async loadMasterAccounts(): Promise<void> {
    // ✓ Prevent duplicate calls
    if (this.isLoadingMasterAccounts) {
      console.warn('loadMasterAccounts already in progress');
      return;
    }

    this.isLoadingMasterAccounts = true;
    this.isLoadingAccounts = true;
    this.masterAccounts = [];
    this.filteredAccounts = [];
    this.searchQuery = '';

    const data = { action: 'List_Masters' };
    const header = { Authorization: localStorage.getItem('t') };

    try {
      const result: APIResponse = await this.apiService.postDataPromis(this.apiURL, data, header);

      // ✓ Proper response validation
      if (result?.status === 1 && result.accIds && Array.isArray(result.accIds)) {
        this.masterAccounts = result.accIds.map((accId: string) => ({
          accId,
          month: moment().format('MM'),
          year: moment().format('YYYY'),
          status: 'ready' as const,
          statusMessage: ''
        }));
        this.filteredAccounts = [...this.masterAccounts];
        this.notifier.alert('Success', '', `${result.accIds.length} accounts loaded`, 'success', 5000);
      } else {
        const errorMsg = result?.error_message || result?.msg || 'Failed to load accounts';
        this.notifier.alert('Info', '', errorMsg, 'info', 5000);
      }
    } catch (error) {
      console.error('Error loading master accounts:', error);
      this.notifier.alert('Error', '', 'Failed to load accounts', 'danger', 5000);
    } finally {
      this.isLoadingAccounts = false;
      this.isLoadingMasterAccounts = false; // ✓ Reset lock
      this.initRowDatepickers();
    }
  }

  // ─── Search ───────────────────────────────────────────────────────────────────
  onSearch(): void {
    const q = this.searchQuery.trim().toLowerCase();
    this.filteredAccounts = q
      ? this.masterAccounts.filter(a => a.accId.toLowerCase().includes(q))
      : [...this.masterAccounts];
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredAccounts = [...this.masterAccounts];
  }

  // ─── Download Single ──────────────────────────────────────────────────────────
  async downloadSingleInvoice(account: MasterAccount): Promise<void> {
    if (!account.month || !account.year) {
      this.notifier.alert('Warning', '', 'Please select both month and year', 'warning', 5000);
      return;
    }

    account.status = 'loading';
    account.statusMessage = 'Downloading...';

    const success = await this.fetchAndDownload(
      {
        action: 'DOWNLOAD_SINGLE_MASTER_ZIP',
        month: account.month,
        year: account.year,
        accId: account.accId
      },
      `invoice_${account.accId}_${account.month}_${account.year}`,
      account
    );

    if (success) {
      account.status = 'success';
      account.statusMessage = '✓ Download started';
      setTimeout(() => {
        account.statusMessage = '✓ Download completed';
        setTimeout(() => { account.status = 'ready'; account.statusMessage = ''; }, 2000);
      }, 1500); // Simulate download completion after 1.5 seconds
    } else {
      account.status = 'error';
      account.statusMessage = '✗ Download failed';
      setTimeout(() => { account.status = 'ready'; account.statusMessage = ''; }, 3000);
    }
  }

  // ─── Download All (Consolidated) ──────────────────────────────────────────────
  async downloadConsolidatedReport(): Promise<void> {
    if (!this.consolidatedForm.valid) {
      this.notifier.alert('Warning', '', 'Please select month and year', 'warning', 5000);
      return;
    }

    this.consolidatedStatus = 'loading';
    this.consolidatedStatusMessage = 'Downloading...';
    this.isDownloadingConsolidated = true;

    const { month, year } = this.consolidatedForm.value;

    const success = await this.fetchAndDownload(
      {
        action: 'DOWNLOAD_ALL_MASTERS_ZIP',
        month,
        year
      },
      `all_accounts_invoices_${month}_${year}`
    );

    if (success) {
      this.consolidatedStatus = 'success';
      this.consolidatedStatusMessage = '✓ Download started';
      this.notifier.alert('Success', '', '✓ Download started', 'success', 5000);

      // ✓ Show completion message after simulated download time
      setTimeout(() => {
        this.consolidatedStatusMessage = '✓ Download completed';
        setTimeout(() => {
          this.consolidatedStatus = 'ready';
          this.consolidatedStatusMessage = '';
          this.isDownloadingConsolidated = false;
        }, 2000);
      }, 1500);
    } else {
      this.consolidatedStatus = 'error';
      this.consolidatedStatusMessage = '✗ Download failed';
      this.isDownloadingConsolidated = false;
      setTimeout(() => {
        this.consolidatedStatus = 'ready';
        this.consolidatedStatusMessage = '';
      }, 3000);
    }
  }

  // ─── Shared Download Core ─────────────────────────────────────────────────────
  private async fetchAndDownload(
    payload: object,
    filenameBase: string,
    account?: MasterAccount
  ): Promise<boolean> {
    const header = { Authorization: localStorage.getItem('t') };

    try {
      const result: APIResponse = await this.apiService.postDataPromis(this.apiURL, payload, header);

      // ✓ Proper response validation with status check
      if (!result) {
        this.handleDownloadError('No response from server', account);
        return false;
      }

      // ✓ Check for error status
      if (result.status === 0 || result.message === 'File not found') {
        this.handleDownloadError(result.message || 'File not found', account);
        return false;
      }

      // ✓ Validate presigned URLs
      if (!result.presignedUrls || !Array.isArray(result.presignedUrls) || result.presignedUrls.length === 0) {
        this.handleDownloadError('No download links available', account);
        return false;
      }

      // ✓ Trigger downloads with proper URL parsing
      result.presignedUrls.forEach((rawUrl: string, i: number) => {
        setTimeout(() => {
          try {
            const url = this.parsePresignedUrl(rawUrl);
            if (!url || !url.startsWith('http')) {
              console.error('Invalid URL parsed:', url);
              return;
            }

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);

            // ✓ Clean up iframe after download
            setTimeout(() => {
              try {
                document.body.removeChild(iframe);
              } catch (e) {
                console.warn('Could not remove iframe:', e);
              }
            }, 5000);

          } catch (err) {
            console.error('Error triggering download:', err);
          }
        }, i * 500);
      });

      return true;

    } catch (err: any) {
      console.error('Download failed:', err);
      this.handleDownloadError(err.message || 'Failed to download', account);
      return false;
    }
  }

  // ─── Download Error Handler ────────────────────────────────────────────────────
  private handleDownloadError(message: string, account?: MasterAccount): void {
    this.notifier.alert('Error', '', message, 'danger', 5000);
    
    if (account) {
      account.status = 'error';
      account.statusMessage = `✗ ${message}`;
    }
  }

  // ─── URL Parser ───────────────────────────────────────────────────────────────
  private parsePresignedUrl(raw: string): string {
    if (!raw) return '';
    
    const trimmed = raw.trim();
    
    // ✓ Extract URL from markdown format: [text](url)
    const markdownMatch = trimmed.match(/\[.*?\]\((https?:\/\/[^)]+)\)/);
    if (markdownMatch) return markdownMatch[1];
    
    // ✓ Direct HTTPS URL
    if (trimmed.startsWith('http')) return trimmed;
    
    // ✓ Extract from parentheses: (url)
    const fallbackMatch = trimmed.match(/\(([^)]+)\)$/);
    if (fallbackMatch) return fallbackMatch[1];
    
    // ✓ As-is if no special format
    return trimmed;
  }

  // ─── Row Datepickers ──────────────────────────────────────────────────────────
  private initRowDatepickers(): void {
    setTimeout(() => {
      this.masterAccounts.forEach((account, i) => {
        const id = `#rowDate_${i}`;
        try {
          $(id).datepicker('destroy');
        } catch (_) {}

        $(id)
          .datepicker({
            autoclose: true,
            minViewMode: 1,
            format: 'mm/yyyy',
            orientation: 'bottom auto',
            endDate: new Date(),
          })
          .on('changeDate', (e: any) => {
            const parsed = moment($(e.target).val(), 'MM/YYYY');
            account.month = parsed.format('MM');
            account.year = parsed.format('YYYY');
          });
      });
    }, 150);
  }

  // ─── Utilities ────────────────────────────────────────────────────────────────
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'loading': return 'info';
      case 'success': return 'success';
      case 'error':   return 'danger';
      default:        return 'secondary';
    }
  }

  getConsolidatedStatusBadgeClass(): string {
    return this.getStatusBadgeClass(this.consolidatedStatus);
  }

  getInitialDateValue(): string {
    return moment().format('MM/YYYY');
  }

  dateParser(date: string): string {
    return moment(date, 'YYYY-MM-DD').format('MMM DD, YYYY');
  }

  toFixed(value: string | number, decimal: number): string {
    return Number(value).toFixed(decimal);
  }
}